import { useState, useMemo, useCallback, useEffect } from "react";
import Card from "react-bootstrap/Card";
import Spinner from "react-bootstrap/Spinner";
import Form from "react-bootstrap/Form";
import { BsCircleFill } from "react-icons/bs";
import SimpleTable from "../../../components/atoms/table/simpleTable/simpleTable";
import "../../../styles/typography.css";
import Button from "../../../components/atoms/button/Button";
import ReusableModal from "../../../components/atoms/modal/ReusableModal";
import {
  getPeerReviewData,
  getTasksData,
  releaseToCustomerFinalData,
  rejectFinalData,
  acceptAndRejectPeerReview,
} from "./tasksApi";
import SecondTab from "../../../components/organisms/secondTab";
import { formatDate } from "../../../utility/Formatdate";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import SampleModalWrapper from "../../../components/organisms/SampleModalWrapper/SampleModalWrapper";
import { getFilterData } from "../../../utility/Filters";
import { downloadReports } from "../Reports/reportsApi";
import PageDescriptionBar from "../../../components/atoms/pageDescriptionBar/PageDescriptionBar";

const getStatusIcon = (row) => {
  return (
    <>
      {row.isGreaterThanTen && (
        <BsCircleFill
          color="#43B324"
          size={10}
          title="Due > 10"
          className="me-1"
        />
      )}
      {row.isGreaterThanFourteen && (
        <BsCircleFill
          color="#E13B3B"
          size={10}
          title="Due > 14"
          className="me-1"
        />
      )}
    </>
  );
};

const Tasks = ({ TabConfig }) => {
  const [loading, setLoading] = useState(false);
  const [showAuditModal, setShowAuditModal] = useState(false);
  const [auditComment, setAuditComment] = useState("");
  const [selectedRowIds, setSelectedRowIds] = useState(new Set());
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showReleaseModal, setShowReleaseModal] = useState(false);
  const [tasksData, setTasksData] = useState([]);
  const [activeTab, setActiveTab] = useState("Final Review");
  const [selectedCompany, setSelectedCompany] = useState("");
  const [selectedProduct, setSelectedProduct] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [sample, setSample] = useState(null);
  // Peer Review state
  const [peerReviewData, setPeerReviewData] = useState([]);
  const [peerReviewPage, setPeerReviewPage] = useState(1);
  const [peerReviewPageSize, setPeerReviewPageSize] = useState(25);
  const [peerReviewTotal, setPeerReviewTotal] = useState(0);
  const [peerReviewFilterText, setPeerReviewFilterText] = useState("");
  const [paginationData, setPaginationData] = useState({});

  const companyOptions = useMemo(() => {
    const uniqueCompanies = getFilterData(
      peerReviewData,
      "company",
      "companyId"
    );
    return uniqueCompanies;
  }, [activeTab, peerReviewData]);

  const productOptions = useMemo(() => {
    const uniqueProducts = getFilterData(
      peerReviewData,
      "product",
      "productId"
    );
    return uniqueProducts;
  }, [activeTab, peerReviewData, tasksData]);

const handlePeerReviewTableChange = async ({
  page = 1,
  pageSize: size = 25,
  search = "",
} = {}) => {
  try {
    const payload = {
      companyId: selectedCompany ? Number(selectedCompany) : 0,
      productId: selectedProduct ? Number(selectedProduct) : 0,
      pageNumber: page,
      pageSize: size,
      status: "",
    };

    if (search) payload.FilterText = search;

    const res = await getPeerReviewData(payload);

    setPaginationData({
      totalRecords: res.totalRecords,
      pageNumber: res.pageNumber,
      pageSize: res.pageSize,
      totalPages: res.totalPages,
    });

    setPeerReviewData(Array.isArray(res.results) ? res.results : []);
    setPeerReviewTotal(res.totalRecords || 0);
    setPeerReviewPage(page);
    setPeerReviewPageSize(size);
    setPeerReviewFilterText(search);

  } catch (error) {
    console.error("[PeerReview] API failed", error);
    setPeerReviewData([]);
    setPeerReviewTotal(0);
  }
};


  useEffect(() => {
    if (activeTab === "Peer Review") {
      handlePeerReviewTableChange({
        page: peerReviewPage,
        pageSize: peerReviewPageSize,
        search: peerReviewFilterText,
        companyId: selectedCompany ? Number(selectedCompany) : 0,
        productId: selectedProduct ? Number(selectedProduct) : 0,
      });
    } else {
      fetchData();
    }
  }, [
    activeTab,
    peerReviewPage,
    peerReviewPageSize,
    peerReviewFilterText,
    selectedCompany,
    selectedProduct,
  ]);

  const fetchData = async (search, currentPage = 1, rowsPerPage = 25) => {
    setLoading(true);
    try {
      const params = {
        PageNumber: currentPage,
        PageSize: rowsPerPage,
      };
      const res = await getTasksData(params);
      const { totalRecords, pageNumber, pageSize, totalPages } = res;
      setPaginationData({ totalRecords, pageNumber, pageSize, totalPages });
      setTasksData(Array.isArray(res.results) ? res.results : []);
    } catch (error) {
      console.error("Error fetching tasks data:", error);
      setTasksData([]);
    }
    setLoading(false);
  };

  const handleView = (row) => {
    const { sampleId } = row;
    setShowModal(true);
    setSample(sampleId);
  };

  // Pagination
  const totalPages = Math.max(1, Math.ceil(tasksData.length / rowsPerPage));
  const paginatedData = useMemo(() => {
    const startIdx = (currentPage - 1) * rowsPerPage;
    return tasksData.slice(startIdx, startIdx + rowsPerPage);
  }, [currentPage, rowsPerPage, tasksData]);

  const isAllSelectedOnPage =
    activeTab === "Peer Review"
      ? peerReviewData.length > 0 &&
        peerReviewData.every((row) => selectedRowIds.has(row.sampleId))
      : tasksData.length > 0 &&
        tasksData.every((row) => selectedRowIds.has(row.sampleId));
  const isAnySelected = selectedRowIds.size > 0;

  const toggleRowSelection = useCallback((id) => {
    setSelectedRowIds((prev) => {
      const copy = new Set(prev);
      if (copy.has(id)) copy.delete(id);
      else copy.add(id);
      return copy;
    });
  }, []);

  const toggleSelectAllOnPage = () => {
    setSelectedRowIds((prev) => {
      const copy = new Set(prev);
      const rows = activeTab === "Peer Review" ? peerReviewData : tasksData;
      if (!isAllSelectedOnPage) {
        rows.forEach((r) => copy.add(r.sampleId));
      } else {
        rows.forEach((r) => copy.delete(r.sampleId));
      }
      return copy;
    });
  };

  const columns = useMemo(
    () => [
      {
        header: (
          <Form.Check
            type="checkbox"
            checked={isAllSelectedOnPage}
            onChange={toggleSelectAllOnPage}
            aria-label="Select all"
          />
        ),
        accessor: "checkbox",
        disableSort: true,
        cell: (row) => (
          <Form.Check
            type="checkbox"
            checked={selectedRowIds.has(row.sampleId)}
            onChange={() => toggleRowSelection(row.sampleId)}
            aria-label={`Select row ${row.sampleId}`}
          />
        ),
      },
      {
        header: "Sample ID",
        accessor: "sampleId",
        cell: (row) => (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span>{row.sampleId}</span>
            {getStatusIcon(row)}
          </div>
        ),
      },
      { header: "Product", accessor: "product" },      
      { header: "Customer Sample ID", accessor: "customerSampleId" },
      {
        header: "Date",
        accessor: "date",
        cell: (row) => formatDate(row.date),
      },
      { header: "Unit", accessor: "unit" },
      { header: "Exp Level", 
        accessor: activeTab === "Peer Review" ? "expLevel" : "expectedLevel"},
      {
        header: "Low Limit",
        accessor: "lowLimit",
        cell: (row) => row.lowLimit.toString(),
      },
      {
        header: "Up Limit",
        accessor: "upLimit",
        cell: (row) => row.upLimit.toString(),
      },
      {
        header: "Assay",
        accessor: "assay",
        cell: (row) => {
          const canEditAssay =
            (row.reAssay1 === undefined ||
              row.reAssay1 === null ||
              row.reAssay1 === "" ||
              row.reAssay1 === 0 ||
              row.reAssay1 === "0") &&
            (row.reAssay2 === undefined ||
              row.reAssay2 === null ||
              row.reAssay2 === "" ||
              row.reAssay2 === 0 ||
              row.reAssay2 === "0") &&
            selectedRowIds.has(row.sampleId);

          const displayValue =
            row.assay === undefined || row.assay === null || row.assay === ""
              ? "-"
              : row.assay;

          if (canEditAssay) {
            return (
              <Form.Control
                size="sm"
                type="text"
                value={row.assay ?? ""}
                onChange={(e) => {
                  setPeerReviewData((prev) =>
                    prev.map((r) =>
                      r.sampleId === row.sampleId
                        ? { ...r, assay: e.target.value }
                        : r
                    )
                  );
                }}
              />
            );
          }
          return <span>{displayValue}</span>;
        },
      },
      { header: "Assay by", accessor: "assayBy" },
      {
        header: "Re-Assay1",
        accessor: "reAssay1",
        cell: (row) => {
          const canEditReAssay1 =
            row.reAssay1 &&
            row.reAssay1 !== 0 &&
            row.reAssay1 !== "0" &&
            (row.reAssay2 === undefined ||
              row.reAssay2 === null ||
              row.reAssay2 === "" ||
              row.reAssay2 === 0 ||
              row.reAssay2 === "0") &&
            selectedRowIds.has(row.sampleId);

          const displayValue =
            row.reAssay1 === undefined ||
            row.reAssay1 === null ||
            row.reAssay1 === "" ||
            row.reAssay1 === 0 ||
            row.reAssay1 === "0"
              ? "-"
              : row.reAssay1;

          if (canEditReAssay1) {
            return (
              <Form.Control
                size="sm"
                type="text"
                value={row.reAssay1 ?? ""}
                onChange={(e) => {
                  setPeerReviewData((prev) =>
                    prev.map((r) =>
                      r.sampleId === row.sampleId
                        ? { ...r, reAssay1: e.target.value }
                        : r
                    )
                  );
                }}
              />
            );
          }
          return <span>{displayValue}</span>;
        },
      },
      { header: "Re-Assay1 by", accessor: "reAssay1By" },
      {
        header: "Re-Assay2",
        accessor: "reAssay2",
        cell: (row) => {
          const canEditReAssay2 =
            row.reAssay2 &&
            row.reAssay2 !== 0 &&
            row.reAssay2 !== "0" &&
            selectedRowIds.has(row.sampleId);

          const displayValue =
            row.reAssay2 === undefined ||
            row.reAssay2 === null ||
            row.reAssay2 === "" ||
            row.reAssay2 === 0 ||
            row.reAssay2 === "0"
              ? "-"
              : row.reAssay2;

          if (canEditReAssay2) {
            return (
              <Form.Control
                size="sm"
                type="text"
                value={row.reAssay2 ?? ""}
                onChange={(e) => {
                  setPeerReviewData((prev) =>
                    prev.map((r) =>
                      r.sampleId === row.sampleId
                        ? { ...r, reAssay2: e.target.value }
                        : r
                    )
                  );
                }}
              />
            );
          }
          return <span>{displayValue}</span>;
        },
      },
      { header: "Re-Assay2 by", accessor: "reAssay2By" },
      {
        header: "Result",
        accessor: "result",
        cell: (row) =>
          row.result !== undefined && row.result !== null ? row.result : "-",
      },
      {
        header: activeTab === "Peer Review" ? "Status" : "Action",
        accessor: activeTab === "Peer Review" ? "nextStatus" : "action",
        cell: (row) =>
          activeTab === "Peer Review" ? (
            row.nextStatus || "-"
          ) : (
            <Button
              variant="edit-disabled"
              size="sm"
              onClick={() => handleView(row)}
            >
              View
            </Button>
          ),
      },
    ],
    [activeTab, selectedRowIds, setPeerReviewData]
  );

  const renderRow = (row) => (
    <tr key={row.sampleId}>
      <td>
        <Form.Check
          type="checkbox"
          checked={selectedRowIds.has(row.sampleId)}
          onChange={() => toggleRowSelection(row.sampleId)}
        />
      </td>
      <td>
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {row.sampleId}
          { getStatusIcon(row)}
        </span>
      </td>
      <td>{row.product || "-"}</td>
      <td>{row.customerSampleId || "-"}</td>
      <td>{formatDate(row.date)}</td>
      <td>{row.unit || "-"}</td>
      <td>

            {activeTab === "Peer Review" ? (
          row.expLevel !== undefined && row.expLevel !== null ? (
            row.expLevel
          ) : (
            "-")
        ) : (
          row.expectedLevel !== undefined && row.expectedLevel !== null ? (
            row.expectedLevel): ("-")

        )}
      </td>
      <td>{row.lowLimit.toString()}</td>
      <td>{row.upLimit.toString()}</td>
      <td>
        {activeTab === "Peer Review" &&
        selectedRowIds.has(row.sampleId) &&
        (row.reAssay1 === undefined ||
          row.reAssay1 === null ||
          row.reAssay1 === "" ||
          row.reAssay1 === 0 ||
          row.reAssay1 === "0") &&
        (row.reAssay2 === undefined ||
          row.reAssay2 === null ||
          row.reAssay2 === "" ||
          row.reAssay2 === 0 ||
          row.reAssay2 === "0") ? (
          <Form.Control
            size="sm"
            type="text"
            value={row.assay ?? ""}
            onChange={(e) => {
              setPeerReviewData((prev) =>
                prev.map((r) =>
                  r.sampleId === row.sampleId
                    ? { ...r, assay: e.target.value }
                    : r
                )
              );
            }}
          />
        ) : row.assay !== null &&
          row.assay !== undefined &&
          row.assay !== "" ? (
          row.assay
        ) : (
          "-"
        )}
      </td>
      <td>{row.assayBy || "-"}</td>
      <td>
        {activeTab === "Peer Review" &&
        selectedRowIds.has(row.sampleId) &&
        row.reAssay1 &&
        !(row.reAssay2 && row.reAssay2 !== 0 && row.reAssay2 !== "0") ? (
          <Form.Control
            size="sm"
            type="text"
            value={row.reAssay1 ?? ""}
            onChange={(e) => {
              setPeerReviewData((prev) =>
                prev.map((r) =>
                  r.sampleId === row.sampleId
                    ? { ...r, reAssay1: e.target.value }
                    : r
                )
              );
            }}
          />
        ) : row.reAssay1 !== null &&
          row.reAssay1 !== undefined &&
          row.reAssay1 !== "" &&
          row.reAssay1 !== 0 &&
          row.reAssay1 !== "0" ? (
          row.reAssay1
        ) : (
          "-"
        )}
      </td>
      <td>{row.reAssay1By || "-"}</td>
      <td>
        {activeTab === "Peer Review" &&
        selectedRowIds.has(row.sampleId) &&
        row.reAssay2 ? (
          <Form.Control
            size="sm"
            type="text"
            value={row.reAssay2 ?? ""}
            onChange={(e) => {
              setPeerReviewData((prev) =>
                prev.map((r) =>
                  r.sampleId === row.sampleId
                    ? { ...r, reAssay2: e.target.value }
                    : r
                )
              );
            }}
          />
        ) : row.reAssay2 !== null &&
          row.reAssay2 !== undefined &&
          row.reAssay2 !== "" &&
          row.reAssay2 !== 0 &&
          row.reAssay2 !== "0" ? (
          row.reAssay2
        ) : (
          "-"
        )}
      </td>
      <td>{row.reAssay2By || "-"}</td>
      <td>
        {row.result !== undefined && row.result !== null ? row.result : "-"}
      </td>
      <td>
        {activeTab === "Peer Review" ? (
          row.nextStatus !== undefined && row.nextStatus !== null ? (
            row.nextStatus
          ) : (
            "-"
          )
        ) : (
          <Button
            variant="edit-disabled"
            size="sm"
            onClick={() => handleView(row)}
          >
            View
          </Button>
        )}
      </td>
    </tr>
  );

  const handleViewReport = async () => {
    try {
      if (selectedRowIds.size === 0) {
        toast.error("Please select at least one sample to view.");
        return;
      }

      const params = {
        sampleIds: Array.from(selectedRowIds),
      };
      const response = await downloadReports(params);

      const blob = new Blob([response], { type: "application/pdf" });
      const viewUrl = window.URL.createObjectURL(blob);

      window.open(viewUrl, "_blank", "noopener,noreferrer");

      setTimeout(() => {
        window.URL.revokeObjectURL(viewUrl);
      }, 60000);

      toast.success("Report opened successfully.");
    } catch (error) {
      toast.error("Failed to open report.");
    }
  };

  const handleConfirmFinalReject = async () => {
    if (selectedRowIds.size === 0) return;
    const payload = {
      sampleIds: Array.from(selectedRowIds),
      comments: auditComment,
    };
    try {
      const res = await rejectFinalData(payload);
      if (res === "success") {
        toast.success("Samples rejected successfully");
        await fetchData();
        setShowRejectModal(false);
        setSelectedRowIds(new Set());
      } else {
        toast.error("Failed to reject samples");
      }
    } catch (error) {
      toast.error("Failed to reject samples");
    }
  };

  const handleConfirmRelease = async () => {
    if (selectedRowIds.size === 0) return;
    const payload = Array.from(selectedRowIds);
    try {
      const res = await releaseToCustomerFinalData(payload);
      if (res) {
        toast.success("Samples released to customer successfully");
        await fetchData();
        setShowReleaseModal(false);
        setSelectedRowIds(new Set());
      } else {
        toast.error("Failed to release samples");
      }
    } catch (error) {
      toast.error("Failed to release samples");
    }
  };

  const handleConfirmAccept = async (isApproved) => {
    if (!isAnySelected) return;

    const peerReviewRequestSample = Array.from(selectedRowIds).map(
      (sampleId) => {
        const row = peerReviewData.find((d) => d.sampleId === sampleId);
        if (row) {
          return {
            sampleId: row.sampleId,
            assayResult: row.assay ?? "",
            reAssay1Result: row.reAssay1 ?? "",
            reAssay2Result: row.reAssay2 ?? "",
            comments: auditComment ?? "",
            nextStatus: row.nextStatus ?? "",
          };
        }
      }
    );
    const payload = {
      peerReviewRequestSample: peerReviewRequestSample.filter((ele) => ele),
      isApproved: isApproved,
    };
    try {
      const res = await acceptAndRejectPeerReview(payload);
      setShowRejectModal(false);
      setAuditComment("");
      if (res) {
        toast.success("Samples Peer Reviewed successfully");

        const payload = {
          PageNumber: currentPage,
          PageSize: rowsPerPage,
          Company: selectedCompany,
          Product: selectedProduct,
        };
        const refreshed = await getPeerReviewData(payload);
        setPeerReviewData(
          Array.isArray(refreshed.results) ? refreshed.results : []
        );
      } else {
        toast.error("Failed to accept samples");
      }
    } catch (error) {
      toast.error("Failed to accept samples");
    }
  };

  return (
    <div>
      <SecondTab tabConfig={TabConfig} />
      <div className="p-3 typography">
        <PageDescriptionBar items={[
          { type: "icon", icon: "📋", text: "Final Review – Perform final sign-off on completed samples" },
          { type: "icon", icon: "👥", text: "Peer Review – Review and validate a peer analyst’s results" },
          { type: "separator" },
          { type: "icon", icon: "☑️", text: "Select – Check rows to approve or reject in bulk" },
          { type: "badge", badge: "active",   text: "Accepted – Sample results approved" },
          { type: "badge", badge: "danger",   text: "Rejected – Sample results flagged for re-analysis" },
        ]} />
        <div
          className="d-flex shadow-lg justify-content-between align-items-center mb-3"
          style={{ background: "#fff", padding: "16px 24px" }}
        >
          <div style={{ display: "flex", gap: 8 }}>
            <Button
              variant={activeTab === "Final Review" ? "active" : "outlined"}
              onClick={() => {
                setActiveTab("Final Review");
                setSelectedRowIds(new Set());
              }}
              style={{ minWidth: 120 }}
            >
              Final Review
            </Button>
            <Button
              variant={activeTab === "Peer Review" ? "active" : "outlined"}
              onClick={() => {
                setActiveTab("Peer Review");
                setSelectedRowIds(new Set());
              }}
              style={{ minWidth: 120 }}
            >
              Peer Review
            </Button>
          </div>
          {activeTab === "Peer Review" ? (
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <label style={{ fontSize: 14, marginRight: 4 }}>Company</label>
              <select
                value={selectedCompany}
                onChange={(e) => {
                  setSelectedCompany(e.target.value);
                  setCurrentPage(1);
                }}
                style={{
                  minWidth: 120,
                  padding: "8px 8px",
                  borderRadius: 4,
                  border: "1px solid #ccc",
                }}
              >
                {companyOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <label style={{ fontSize: 14, marginLeft: 16, marginRight: 4 }}>
                Product
              </label>
              <select
                value={selectedProduct}
                onChange={(e) => {
                  setSelectedProduct(e.target.value);
                  setCurrentPage(1);
                }}
                style={{
                  minWidth: 120,
                  padding: "8px 10px",
                  borderRadius: 4,
                  border: "1px solid #ccc",
                }}
              >
                {productOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <div
                className="line-separator"
                style={{
                  width: "2px",
                  height: "46px",
                  margin: "0 16px",
                  backgroundColor: "rgba(0, 0, 0, 0.3)",
                }}
              />
              <Button
                variant={isAnySelected ? "outlined" : "disabled"}
                disabled={!isAnySelected}
                onClick={() => isAnySelected && setShowAuditModal(true)}
              >
                Reject
              </Button>
              <Button
                variant={isAnySelected ? "outlined" : "disabled"}
                disabled={!isAnySelected}
                onClick={() => isAnySelected && setShowRejectModal(true)}
              >
                Accept
              </Button>
            </div>
          ) : (
            <div>
              <Button
                variant={isAnySelected ? "outlined" : "disabled"}
                className="me-2"
                disabled={!isAnySelected}
                onClick={() => isAnySelected && setShowAuditModal(true)}
              >
                Remove
              </Button>
                <Button
                  className="me-2"
                  key="ViewReport"
                  size="md"
                  variant={isAnySelected ? "outlined" : "disabled"}
                  disabled={!isAnySelected}
                  onClick={handleViewReport}
                >
                  View Report
                </Button>
              <Button
                variant={isAnySelected ? "outlined" : "disabled"}
                disabled={!isAnySelected}
                onClick={() => isAnySelected && setShowReleaseModal(true)}
              >
                Release to Customer
              </Button>
              <span className="ms-3 me-2">
                <BsCircleFill color="#43B324" size={10} className="me-1" /> Due
                &gt; 10
              </span>
              <span>
                <BsCircleFill color="#E13B3B" size={10} className="me-1" /> Due
                &gt; 14
              </span>
            </div>
          )}
        </div>
        <Card>
          <Card.Body className="p-0">
            <div className="table-responsive">
              {loading ? (
                <div style={{ textAlign: "center", padding: "60px 0" }}>
                  <Spinner animation="border" /> Loading...
                </div>
              ) : (
                <SimpleTable
                  columns={columns}
                  data={
                    activeTab === "Peer Review" ? peerReviewData : tasksData
                  }
                  rowsPerPage={
                    activeTab === "Peer Review"
                      ? peerReviewPageSize
                      : rowsPerPage
                  }
                  customRowRender={(row) => renderRow(row)}
                  showPagination={true}
                  currentPage={
                    activeTab === "Peer Review" ? peerReviewPage : currentPage
                  }
                  totalPages={
                    activeTab === "Peer Review"
                      ? Math.max(
                          1,
                          Math.ceil(peerReviewTotal / peerReviewPageSize)
                        )
                      : totalPages
                  }
                 onPageChange={(newPage) => {
                    if (activeTab === "Peer Review") {
                      setPeerReviewPage(newPage);
                      handlePeerReviewTableChange({
                        page: newPage,
                        pageSize: peerReviewPageSize,
                        search: peerReviewFilterText,
                      });
                    } else {
                      setCurrentPage(newPage);
                      fetchData("", newPage, rowsPerPage);
                    }
                  }}
                 onRowsPerPageChange={(newSize) => {
                    if (activeTab === "Peer Review") {
                      setPeerReviewPageSize(newSize);
                      setPeerReviewPage(1);
                      handlePeerReviewTableChange({
                        page: 1,
                        pageSize: newSize,
                        search: peerReviewFilterText,
                      });
                    } else {
                      setRowsPerPage(newSize);
                      setCurrentPage(1);
                      fetchData("", 1, newSize);
                    }
                  }}
                  paginationData={paginationData}
                  searchValue={""}
                  fetchData={
  activeTab === "Peer Review"
    ? (search, page, size) =>
        handlePeerReviewTableChange({ search, page, pageSize: size })
    : (search, page, size) => fetchData(search, page, size)
}

                />
              )}
            </div>
          </Card.Body>
        </Card>
      </div>
      {/* Audit Modal for Accept */}
      <ReusableModal
        show={showAuditModal}
        onHide={() => setShowAuditModal(false)}
        title="Confirm Reject"
        size="md"
        buttons={[
          {
            text: "Cancel",
            variant: "outlined",
            onClick: () => setShowAuditModal(false),
          },
          {
            text: "Update",
            variant: "active",
            onClick: () => {
              setShowAuditModal(false);
              activeTab === "Peer Review"
                ? handleConfirmAccept(false)
                : handleConfirmFinalReject();
            },
          },
        ]}
      >
        <div>
          <p>
            Are you sure you want to reject the value? If yes, please provide a
            comment for this change. It will be recorded in the audit trail.
          </p>
          <textarea
            className="form-control"
            rows={4}
            placeholder="Comments..."
            value={auditComment}
            onChange={(e) => setAuditComment(e.target.value)}
          />
        </div>
      </ReusableModal>

      {/* Reject Confirmation Modal */}
      <ReusableModal
        show={showRejectModal}
        size="md"
        onHide={() => setShowRejectModal(false)}
        title="Confirm Accept"
        buttons={[
          {
            text: "Cancel",
            variant: "outlined",
            onClick: () => setShowRejectModal(false),
          },
          {
            text: "Ok",
            variant: "active",
            onClick: () => handleConfirmAccept(true),
          },
        ]}
      >
        <div>
          <p>
            Are you sure you want to accept the selected sample
            {selectedRowIds.size > 1 ? "s" : ""}? If yes, please provide a
            comment for this change. It will be recorded in the audit trail.
          </p>
          <textarea
            className="form-control"
            rows={4}
            placeholder="Comments..."
            value={auditComment}
            onChange={(e) => setAuditComment(e.target.value)}
          />
        </div>
      </ReusableModal>

      {/* Release Confirmation Modal */}
      <ReusableModal
        show={showReleaseModal}
        onHide={() => setShowReleaseModal(false)}
        title="Confirm Release"
        buttons={[
          {
            text: "Cancel",
            variant: "outlined",
            onClick: () => setShowReleaseModal(false),
          },
          { text: "Ok", variant: "active", onClick: handleConfirmRelease },
        ]}
      >
        <p>
          Are you sure you want to release the selected sample
          {selectedRowIds.size > 1 ? "s" : ""} to customer?
        </p>
      </ReusableModal>
      <SampleModalWrapper
        sampleID={sample}
        showModal={showModal}
        setShowModal={setShowModal}
        setSampleID={setSample}
      />

      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
};

export default Tasks;
